import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Stack, Typography, Box, Chip, Radio, RadioGroup,
  FormControlLabel, CircularProgress, Paper, Alert,
  InputAdornment, IconButton, Switch, Tooltip,
} from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import { apiGet, apiPost, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

// Quick-pick package presets the operator can tap to fill in dimensions
// instantly. These are the boxes the user explicitly told us they
// stock. Sorted ascending by volume so the smallest box (used most
// often) is the leftmost tap target.
const PACKAGE_SIZES = [
  { label: '6 × 5 × 4',   length: 6,  width: 5,  height: 4 },
  { label: '9 × 6 × 3',   length: 9,  width: 6,  height: 3 },
  { label: '8 × 8 × 5',   length: 8,  width: 8,  height: 5 },
  { label: '7 × 7 × 7',   length: 7,  width: 7,  height: 7 },
  { label: '12 × 9 × 6',  length: 12, width: 9,  height: 6 },
  { label: '14 × 14 × 4', length: 14, width: 14, height: 4 },
].sort((a, b) => (a.length * a.width * a.height) - (b.length * b.width * b.height));

// (Seller-Hub flow doesn't take a packageType up front — eBay returns
// every carrier/service that fits the entered dimensions. We removed
// the PACKAGE_TYPES dropdown that lived here when we were on the dev
// API path.)

const money = (n, cur = 'USD') =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}${cur && cur !== 'USD' ? ' ' + cur : ''}`;

// Carrier abbreviations are inconsistent on eBay's side (e.g. they
// return USPS / UPS / FEDEX in some places and USPS_PRIORITY in
// others). This humanizes the codes for the rate list.
function carrierLabel(code) {
  if (!code) return '—';
  const s = String(code).toUpperCase();
  if (s.includes('USPS')) return 'USPS';
  if (s.includes('UPS')) return 'UPS';
  if (s.includes('FEDEX') || s.includes('FED_EX')) return 'FedEx';
  if (s.includes('DHL')) return 'DHL';
  return code;
}

// Canonical bucket for carrier-coloring + the unfulfilled-page counter.
// Anything we don't recognize falls into "other" and renders neutral.
function carrierBucket(code) {
  if (!code) return 'other';
  const s = String(code).toUpperCase();
  if (s.includes('USPS')) return 'usps';
  if (s.includes('FEDEX') || s.includes('FED_EX')) return 'fedex';
  if (s.includes('UPS')) return 'ups';
  return 'other';
}

// Theme-friendly accents per carrier. Borders go on rate cards so the
// list is colour-batchable at a glance; chip ``color`` props pick up
// the same palette where Material UI already maps them. ``hex`` is
// used for the left edge stripe so USPS stays USPS-blue and FedEx
// stays FedEx-purple regardless of the active MUI theme.
const CARRIER_STYLE = {
  usps:  { color: 'info',      hex: '#004B87' },  // USPS blue
  fedex: { color: 'secondary', hex: '#4D148C' },  // FedEx purple
  ups:   { color: 'warning',   hex: '#7B5B26' },  // UPS brown
  other: { color: 'default',   hex: '#9E9E9E' },
};

// ── Listing-level localStorage cache for weight + dims ─────────────
// Server already persists per-listing dims (items.pkg_* + external_label_log)
// but those only update after a successful purchase, and other unfulfilled
// cards on the page won't reflect the new value until the next page refresh.
// The local cache fills that gap: the moment a label is bought (or the
// operator types dims and clicks Get rates), the entry is available for
// any sibling card opened next — including across browser reloads.
//
// Stored shape: { weight_oz_per_unit, length_in, width_in, height_in,
//                 package_type, updated_at }. Weight is per-unit so a
// follow-on qty=N sale prefills weight as ``per_unit × N``.
const LISTING_DIMS_LS_PREFIX = 'bidrl:listingDims:';

// Same slug normalization as backend's _inventory_item_key
// (flask_app.py:5303). Used to group sibling lots that don't share an
// eBay legacyItemId but share a product title.
function inventoryKey(title) {
  if (!title) return '';
  let s = String(title).normalize('NFKC').toLowerCase();
  s = s.replace(/[‐-―−⁃⁓]/g, '-');
  s = s.replace(/[^\w\s-]/gu, '');
  s = s.replace(/\s+/g, '-');
  s = s.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  s = s.slice(0, 200);
  return s || 'untitled';
}

// Listing identity for the dims cache. External rows carry the eBay
// legacyItemId on the row payload; native BidRL rows fall back to the
// inventory-key slug of the cleaned title (same key the backend uses
// when propagating dims to sibling lots in shipping_purchase).
function listingDimsKey(row) {
  if (!row) return '';
  if (row.legacy_item_id) return `ext:${row.legacy_item_id}`;
  const slug = inventoryKey(row.cleaned_title || row.title || '');
  return slug ? `inv:${slug}` : '';
}

function loadListingDims(row) {
  const k = listingDimsKey(row);
  if (!k || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LISTING_DIMS_LS_PREFIX + k);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// eBay's blanket default for listings without per-listing dims set in
// Seller Hub: 2 lb (32 oz) + 12×8×6. When /api/listing_dims hands back
// exactly this combo, the seller almost certainly never entered a real
// value — eBay just stamped its global default on the listing. Treat
// that as "no eBay value" so the dialog falls back to the operator's
// last-saved local cache (or our own default below) instead of clobbering
// good local data with a synthetic listing-level placeholder.
const EBAY_DEFAULT_PROFILE = {
  weight_oz: 32, length_in: 12, width_in: 8, height_in: 6,
};
function isEbayDefaultProfile(d) {
  if (!d) return false;
  return Number(d.weight_oz || 0) === EBAY_DEFAULT_PROFILE.weight_oz
      && Number(d.length_in || 0) === EBAY_DEFAULT_PROFILE.length_in
      && Number(d.width_in  || 0) === EBAY_DEFAULT_PROFILE.width_in
      && Number(d.height_in || 0) === EBAY_DEFAULT_PROFILE.height_in;
}

// Last-resort prefill when neither the local cache nor a unique eBay
// value is available. 12×9×6 is one of the operator's preset boxes;
// 2 lb is a reasonable guess for an unknown package weight.
const FALLBACK_PREFILL = {
  weight_oz: 32, length_in: 12, width_in: 9, height_in: 6,
};

function saveListingDims(row, dims) {
  const k = listingDimsKey(row);
  if (!k || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      LISTING_DIMS_LS_PREFIX + k,
      JSON.stringify({ ...dims, updated_at: Date.now() }),
    );
  } catch {
    // Quota / private-mode failures — non-fatal.
  }
}

function serviceLabel(s) {
  if (!s) return '';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * <ShipLabelDialog
 *   open                bool
 *   row={...}           a row from /api/unfullfilled_sales
 *   onClose()           dialog dismiss
 *   onPurchased(result) called after a successful purchase so the parent
 *                       can refresh the list and remove the card
 *   onSubmitAsync(payload)
 *                       optional. When provided, the Buy button hands the
 *                       purchase payload off to the parent and closes
 *                       immediately without awaiting eBay's response —
 *                       lets the operator advance to the next order
 *                       while the label is being purchased in the
 *                       background. The parent owns retry/error UI (see
 *                       UnfulfilledSales pendingPurchases handling).
 *   queueRemaining      optional integer. When >0, replaces the top-right
 *                       chip so the operator knows how many orders are
 *                       still queued behind this one in a bulk-ship
 *                       session.
 *   sharedDims          optional ``{ weight_oz_per_unit, length_in,
 *                       width_in, height_in }``. When provided, the
 *                       dialog skips step 1 entirely (dims are pre-filled
 *                       from these values, scaled by ``row.order_qty``)
 *                       and auto-fetches rates on open. The operator can
 *                       still click "Edit dimensions" on the rate screen
 *                       to change dims for this one order or for the rest
 *                       of the queue.
 *   onDimsCommitted(dims)
 *                       optional. Called when the operator commits dims
 *                       by clicking Get rates. Parent stores the values
 *                       and passes them back as ``sharedDims`` on the
 *                       next order in a bulk-ship queue.
 * />
 *
 * Three internal steps: (1) enter dims, (2) pick a rate, (3) success.
 * (Step 3 is skipped in async mode — success is a toast.)
 */
export default function ShipLabelDialog({
  open, row, onClose, onPurchased,
  onSubmitAsync, queueRemaining,
  sharedDims, onDimsCommitted,
}) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [step, setStep] = useState(1);

  // ── Step 1 inputs — pre-filled from (in priority order):
  //   1. localStorage listing-dims cache (most-recent operator entry
  //      on this device for this same listing). Stored per-unit so a
  //      follow-on qty=N sale of the same listing prefills weight as
  //      per_unit × N. Dimensions stay as-is (same box).
  //   2. row.listing_dims (server-side items.pkg_*). Treated as total
  //      package weight — no qty multiplier (legacy semantic). Skipped
  //      when it exactly matches eBay's global default (see
  //      isEbayDefaultProfile) — that combo means the seller never
  //      entered a real value and we'd rather fall through to our own
  //      default than overwrite a real local entry with a placeholder.
  //   3. row.package_preset (per-lot fallback). Same as #2.
  //   4. FALLBACK_PREFILL (2 lb / 12×9×6) so the inputs are never empty.
  const listingDims  = row?.listing_dims || {};
  const preset       = row?.package_preset || {};
  const localCached  = row ? loadListingDims(row) : null;
  const orderQty     = Math.max(Number(row?.order_qty || 1), 1);
  // Strip out row-side dim sources that look like eBay's placeholder so
  // they don't show up as "filled from items table" when really the
  // seller never entered anything.
  const listingDimsUsable = isEbayDefaultProfile(listingDims) ? {} : listingDims;
  const presetUsable      = isEbayDefaultProfile(preset)      ? {} : preset;
  const prefill = (() => {
    if (localCached) {
      const w = localCached.weight_oz_per_unit;
      return {
        weight_oz: w != null ? w * orderQty : FALLBACK_PREFILL.weight_oz,
        length_in: localCached.length_in ?? FALLBACK_PREFILL.length_in,
        width_in:  localCached.width_in  ?? FALLBACK_PREFILL.width_in,
        height_in: localCached.height_in ?? FALLBACK_PREFILL.height_in,
      };
    }
    return {
      weight_oz: listingDimsUsable.weight_oz ?? presetUsable.weight_oz ?? FALLBACK_PREFILL.weight_oz,
      length_in: listingDimsUsable.length_in ?? presetUsable.length_in ?? FALLBACK_PREFILL.length_in,
      width_in:  listingDimsUsable.width_in  ?? presetUsable.width_in  ?? FALLBACK_PREFILL.width_in,
      height_in: listingDimsUsable.height_in ?? presetUsable.height_in ?? FALLBACK_PREFILL.height_in,
    };
  })();
  const prefillFromListing = Boolean(
    listingDimsUsable.weight_oz || listingDimsUsable.length_in ||
    listingDimsUsable.width_in  || listingDimsUsable.height_in
  );
  const prefillFromLocal = Boolean(localCached);

  const [weightLb, setWeightLb] = useState("");
  const [weightOzExtra, setWeightOzExtra] = useState("");  // ounces above the pounds, 0-15
  const [length, setLength] = useState("");
  const [width,  setWidth]  = useState("");
  const [height, setHeight] = useState("");
  // The chip label needs to reflect where the displayed dims ACTUALLY came
  // from (eBay live, our local cache, a sibling-lot cache, or just the
  // preset). The backend tells us this in the `source` field of
  // /api/listing_dims/<lot_id>; we mirror it here so the chip can stop
  // lying. Old code labeled everything "Filled from eBay listing"
  // regardless of whether the values came from the cache snapshot — which
  // hid the case where eBay's stored dims differ from what was actually
  // entered on the listing edit form.
  const [fetchedFromListing, setFetchedFromListing] = useState(false);
  const [dimsSource, setDimsSource] = useState(null);  // 'ebay_inventory' | 'local' | 'sibling_local' | 'preset' | null
  const [fetchingDims, setFetchingDims] = useState(false);

  // Helper to apply a {weight_oz, length_in, width_in, height_in}
  // payload to the input state. Reused by the initial preset prefill
  // and the on-open eBay-inventory fetch.
  const applyDims = (d) => {
    const oz = Number(d?.weight_oz || 0);
    if (oz > 0) {
      // Snap to nearest 0.1 oz, then collapse a "round number with
      // floating-point rounding drift" (e.g. 4.96 from eBay Inventory's
      // 2.31 lb → 36.96 oz when the listing was actually 5.0 oz) to a
      // clean integer. Most listings entered via eBay's listing-form
      // use integer oz; the .96/.05 fractional remainders are unit
      // conversion noise, not real precision.
      const snapped = Math.round(oz * 10) / 10;
      const lbInt = Math.floor(snapped / 16);
      let ozExtra = snapped - lbInt * 16;
      if (Math.abs(ozExtra - Math.round(ozExtra)) < 0.15) {
        ozExtra = Math.round(ozExtra);
      }
      // toFixed(2) tames float noise (5.200000000003 → "5.20"); the
      // regex then strips trailing-zero fractional padding. "0.00" →
      // "0", "5.20" → "5.2", "5.00" → "5".
      const ozStr = ozExtra.toFixed(2).replace(/\.?0+$/, "") || "0";
      setWeightLb(String(lbInt));
      setWeightOzExtra(ozStr);
    }
    if (d?.length_in) setLength(String(d.length_in));
    if (d?.width_in)  setWidth (String(d.width_in));
    if (d?.height_in) setHeight(String(d.height_in));
  };

  // Set by the open effect when sharedDims is supplied; consumed by a
  // separate effect that runs fetchQuote once dim state has settled.
  const [autoQuotePending, setAutoQuotePending] = useState(false);

  // Reset state every time the dialog opens on a new row.
  useEffect(() => {
    if (!open || !row) return;
    setStep(1);
    setRates([]); setQuoteId(null); setSelectedRateId(null);
    setCarrierFilter(null);
    setPurchased(null);
    setPurchaseError(null);
    setQuoteError(null);
    setSessionExpired(false);
    setFetchedFromListing(false);
    setAutoQuotePending(false);

    // Bulk-ship path — shared dims from the parent's queue win over any
    // per-row cache. Scale per-unit weight by ``order_qty`` just like
    // the listing-dims cache does, so a 2-lb-per-unit shared dim gives
    // a 6-lb weight on a qty=3 order without the operator retyping.
    if (sharedDims && (
      sharedDims.weight_oz_per_unit > 0
      || sharedDims.length_in > 0
      || sharedDims.width_in > 0
      || sharedDims.height_in > 0
    )) {
      setDimsSource('bulk_shared');
      const perUnit = Number(sharedDims.weight_oz_per_unit) || 0;
      const totalOz = perUnit * (Math.max(orderQty, 1));
      if (totalOz > 0) {
        setWeightLb(String(Math.floor(totalOz / 16)));
        setWeightOzExtra(String((totalOz % 16).toFixed(2).replace(/\.?0+$/, "")));
      } else {
        setWeightLb(""); setWeightOzExtra("");
      }
      setLength(sharedDims.length_in ? String(sharedDims.length_in) : "");
      setWidth (sharedDims.width_in  ? String(sharedDims.width_in)  : "");
      setHeight(sharedDims.height_in ? String(sharedDims.height_in) : "");
      // Skip step 1 entirely — the operator has already committed dims
      // for this queue. A "Edit dimensions" button in step 2's header
      // sends them back here if this one order needs a different box.
      setAutoQuotePending(true);
      // Skip the eBay listing_dims fetch — the bulk operator has
      // deliberately overridden all per-listing values, and letting the
      // async fetch overwrite the shared dims mid-quote would be
      // confusing.
      return;
    }

    // Initial source for the chip — reflects what `prefill` came from.
    // Will get overwritten if the async eBay-inventory fetch below
    // succeeds with a different source. Uses the *Usable variants so an
    // eBay-default placeholder doesn't masquerade as a real items-table
    // value.
    if (localCached) setDimsSource('local_cache');
    else if (prefillFromListing) setDimsSource('items_table');
    else if (presetUsable.weight_oz || presetUsable.length_in || presetUsable.width_in || presetUsable.height_in) setDimsSource('preset');
    else setDimsSource('fallback_default');

    // Pre-fill from listing_dims (on-row) or preset, in priority order.
    const oz = Number(prefill.weight_oz || 0);
    if (oz > 0) {
      setWeightLb(String(Math.floor(oz / 16)));
      setWeightOzExtra(String((oz % 16).toFixed(2).replace(/\.?0+$/, "")));
    } else {
      setWeightLb(""); setWeightOzExtra("");
    }
    setLength(prefill.length_in ? String(prefill.length_in) : "");
    setWidth (prefill.width_in  ? String(prefill.width_in)  : "");
    setHeight(prefill.height_in ? String(prefill.height_in) : "");

    // Ask the backend for the live eBay listing dims, but only let it
    // overwrite the inputs when:
    //   * there's no local cache for this listing (operator-entered
    //     values always win — eBay's stored value may be a stale
    //     auto-default from the listing form), AND
    //   * the returned value isn't eBay's blanket placeholder
    //     (2 lb / 12×8×6), which would clobber our fallback with a
    //     synthetic number the seller never actually typed.
    // Skipping the fetch entirely when localCached is set avoids the
    // jarring "value flips a moment after the dialog opens" UX.
    if (row.lot_id && !localCached) {
      let cancelled = false;
      setFetchingDims(true);
      // Pass the order's actual line-item SKU so the backend queries
      // the exact eBay listing the buyer ordered from. Without this the
      // backend falls back to a sibling-lot guess which can pick a
      // completely different listing with stale dims.
      const url = row.ebay_sku
        ? `/api/listing_dims/${encodeURIComponent(row.lot_id)}?sku=${encodeURIComponent(row.ebay_sku)}`
        : `/api/listing_dims/${encodeURIComponent(row.lot_id)}`;
      apiGet(url)
        .then((d) => {
          if (cancelled) return;
          if (isEbayDefaultProfile(d)) {
            // eBay handed back its own placeholder — pretend the
            // listing has no recorded dims so the chip reflects the
            // fallback (and the source label isn't a lie).
            return;
          }
          // Update the source label regardless of whether values
          // changed — even a confirming "yes, these match eBay"
          // response is useful info for the operator.
          if (d?.source) setDimsSource(d.source);
          if (d?.source === "ebay_inventory" &&
              (d.weight_oz || d.length_in || d.width_in || d.height_in)) {
            applyDims(d);
            setFetchedFromListing(true);
          }
        })
        .catch(() => { /* silently fall back to whatever's prefilled */ })
        .finally(() => { if (!cancelled) setFetchingDims(false); });
      return () => { cancelled = true; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.lot_id, row?.order_id]);

  // ── Step 2 state
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  // Set when the backend returns needs_session_sync (428). Surfaces a
  // big "open Settings → re-sync cookies" banner so the operator
  // doesn't have to puzzle out what "eBay returned 403" means.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [rates, setRates] = useState([]);
  const [quoteId, setQuoteId] = useState(null);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  // Carrier filter for the rate list. ``null`` = show every carrier.
  // Reset every time the dialog reopens on a new row (see effect below).
  const [carrierFilter, setCarrierFilter] = useState(null);
  // Signature-confirmation + shipping-insurance opt-ins. Both auto-
  // enable on high-value orders so the operator doesn't have to
  // remember; marginal cost is small compared to the loss exposure.
  // Same threshold for both; bump if/when you want different defaults.
  const SIGNATURE_AUTO_THRESHOLD = 500;
  const INSURANCE_AUTO_THRESHOLD = 500;
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [insuranceRequired, setInsuranceRequired] = useState(false);
  // Re-evaluate the defaults whenever the dialog opens on a new order
  // (or the order_total finishes loading). Operator can override either
  // direction; subsequent toggles aren't reset by this effect because
  // it only fires on row change.
  useEffect(() => {
    if (!open) return;
    const total = Number(row?.sold_price ?? row?.sold_price_total ?? 0);
    setSignatureRequired(total > SIGNATURE_AUTO_THRESHOLD);
    setInsuranceRequired(total > INSURANCE_AUTO_THRESHOLD);
  }, [open, row?.order_id, row?.sold_price]);

  // ── Step 3 state — populated from /api/shipping_purchase response:
  //   { shipment_id, tracking_number, carrier_code, carrier_name,
  //     service_name, label_cost, label_currency, label_url, qr_code_url }
  const [purchased, setPurchased] = useState(null);
  // Failed purchase — has the human message + (optional) the raw eBay
  // response body so the operator can see exactly what went wrong
  // without ssh-ing to the server log.
  const [purchaseError, setPurchaseError] = useState(null);

  // Extension health — probed once when the dialog opens so we can
  // show a yellow "extension not running on this machine" banner.
  // null until probed, then {reachable, sumatra_found, printer_name, ...}.
  //
  // IMPORTANT: this probe goes from the BROWSER straight to
  // http://127.0.0.1:5175 (the print-server Flask on the operator's
  // own laptop). It does NOT go through BidRL's backend, because
  // BidRL Flask runs on a remote server whose localhost is not the
  // operator's localhost. Without this, the proxy variant would
  // always show "extension not running" regardless of whether it is.
  //
  // The fetch needs CORS on the label_server.py side (allow-origin: *),
  // which is in place. AbortController gives us a clean timeout so a
  // hung extension doesn't stall the dialog forever.
  const [extHealth, setExtHealth] = useState(null);
  // True once we've observed `last_saved_path` change AFTER the purchase
  // fires — concrete proof the print actually landed on label_server.py.
  // Pre-purchase the value is whatever the last print was (or null), so
  // we don't want to mistake that for "the new label printed". The
  // baseline is captured on dialog open and the post-purchase poll
  // flips this true when the value moves off the baseline.
  const [printDetected, setPrintDetected] = useState(false);
  const [printBaseline, setPrintBaseline] = useState(null);
  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    fetch("http://127.0.0.1:5175/api/ebay/health", {
      method: "GET",
      mode: "cors",
      signal: ctrl.signal,
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        setExtHealth({
          reachable: true,
          sumatra_found:  !!data?.sumatra_found,
          printer_name:    data?.printer_name,
          last_saved_path: data?.last_saved_path,
          save_dir:        data?.save_dir,
          time:            data?.time,
        });
        // Baseline = last_saved_path observed BEFORE this purchase.
        // Anything different after we buy = the new label landed.
        setPrintBaseline(data?.last_saved_path || null);
        setPrintDetected(false);
      })
      .catch((e) => setExtHealth({
        reachable: false,
        error: e?.name === "AbortError"
          ? "probe timed out"
          : (typeof e === "number" ? `HTTP ${e}` : "no response from 127.0.0.1:5175"),
      }))
      .finally(() => clearTimeout(t));
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [open]);

  // After a successful purchase, poll the local label_server every 1s
  // for up to 30s waiting for `last_saved_path` to change off the
  // baseline. When it does, we know the extension forwarded the PDF
  // and the print fired — and we surface "Label received by local
  // print server" with the exact path it landed at. Without this the
  // dialog used to just say "extension is running" forever, which
  // didn't actually confirm the print landed.
  useEffect(() => {
    if (step !== 3 || !purchased || !extHealth?.reachable || printDetected) return;
    let cancelled = false;
    const deadline = Date.now() + 30_000;
    const tick = async () => {
      if (cancelled || Date.now() > deadline) return;
      try {
        const r = await fetch("http://127.0.0.1:5175/api/ebay/health",
                              { method: "GET", mode: "cors" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const cur = data?.last_saved_path || null;
        // Update extHealth so the UI's saved-path readout stays fresh.
        setExtHealth((prev) => ({ ...(prev || {}),
          last_saved_path: cur,
          printer_name:    data?.printer_name,
          sumatra_found:   !!data?.sumatra_found,
          save_dir:        data?.save_dir,
        }));
        if (cur && cur !== printBaseline) {
          setPrintDetected(true);
          return;  // stop polling
        }
      } catch {
        // Ignore — local server is on the same machine, transient
        // failures are unlikely and we'll just retry next tick.
      }
      setTimeout(tick, 1000);
    };
    const id = setTimeout(tick, 1000);
    return () => { cancelled = true; clearTimeout(id); };
  }, [step, purchased, extHealth?.reachable, printDetected, printBaseline]);

  const weightOzTotal = useMemo(() => {
    const lb = parseFloat(weightLb) || 0;
    const oz = parseFloat(weightOzExtra) || 0;
    return Math.max(0, lb * 16 + oz);
  }, [weightLb, weightOzExtra]);

  // Toggle-aware effective total per rate (base + signature surcharge
  // when on + insurance surcharge when on). Drives both the displayed
  // sort and the "cheapest first" promise — without this the operator
  // could toggle insurance on, find the cheapest base-cost rate, and
  // unknowingly pay more than a different rate whose insurance tier is
  // cheaper.
  const effectiveTotal = (r) => {
    const base = Number(r.base_cost) || 0;
    const sig = signatureRequired && Number(r.signature_cost) > 0 ? Number(r.signature_cost) : 0;
    const ins = insuranceRequired && Number(r.insurance_cost) > 0 ? Number(r.insurance_cost) : 0;
    return base + sig + ins;
  };
  const sortedRates = useMemo(() => {
    const copy = (rates || []).slice();
    copy.sort((a, b) => effectiveTotal(a) - effectiveTotal(b));
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, signatureRequired, insuranceRequired]);

  const dimsValid = Number(length) > 0 && Number(width) > 0 && Number(height) > 0;
  const weightValid = weightOzTotal > 0;

  const shipTo = row?.ship_to || {};
  const shipToValid = !!(shipTo.address1 && shipTo.city && shipTo.state && shipTo.postal_code);

  // Bulk-ship auto-quote: when the open effect flagged this row as a
  // shared-dims continuation of a queue, fire fetchQuote as soon as
  // state has stabilised (dims valid, address valid, not already
  // quoting). fetchQuote itself flips setStep(2) on success.
  useEffect(() => {
    if (!autoQuotePending || !open) return;
    if (quoting || rates.length > 0) return;
    if (!weightValid || !dimsValid || !shipToValid) return;
    setAutoQuotePending(false);
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoQuotePending, open, weightValid, dimsValid, shipToValid, quoting, rates.length]);

  const applyPreset = (p) => {
    setLength(String(p.length));
    setWidth (String(p.width));
    setHeight(String(p.height));
  };

  const fetchQuote = async () => {
    if (!weightValid) { toastError("Weight required", "Enter pounds + ounces"); return; }
    if (!dimsValid)   { toastError("Dimensions required", "All three sides must be positive"); return; }
    if (!shipToValid) { toastError("Buyer address missing", "We couldn't read the destination address from this order"); return; }
    setQuoting(true);
    setQuoteError(null);
    setSessionExpired(false);
    try {
      // ``order_total`` = the buyer's grand total (used for the orders[].
      // totalPaid field eBay attaches to the quote). ``insured_value`` =
      // the merchandise subtotal (priceSubtotal), which is what eBay's
      // own UI sets as insuredValue.amount.value — quoting insurance at
      // the line item's value alone underinsured multi-unit orders by
      // 3-4x (operator-reported $16 vs ~$43 expected). Falls back
      // through total_paid → sold_price so single-line / native rows
      // without the subtotal still quote at the closest sensible value.
      const insuredValueDefault =
        Number(row.merchandise_value) > 0
          ? Number(row.merchandise_value)
          : (Number(row.total_paid) > 0
            ? Number(row.total_paid)
            : (Number(row.sold_price) > 0 ? Number(row.sold_price) : null));
      const data = await apiPost('/api/shipping_quote', {
        order_id:     row.order_id,
        line_item_id: row.line_item_id,
        order_qty:    row.order_qty || 1,
        ship_to:      shipTo,
        weight_oz:    weightOzTotal,
        length_in:    parseFloat(length),
        width_in:     parseFloat(width),
        height_in:    parseFloat(height),
        order_total:  row.total_paid ?? row.sold_price ?? null,
        insured_value: insuredValueDefault,
      });
      const rs = Array.isArray(data?.rates) ? data.rates : [];
      if (rs.length === 0) {
        setQuoteError("eBay returned zero usable rates for this package. Try slightly different dimensions or weight.");
      }
      setQuoteId(data?.shipping_quote_id || null);
      setRates(rs);
      setSelectedRateId(rs[0]?.rate_id || null);
      if (rs.length > 0) setStep(2);
      // Notify parent of the committed dims so a bulk-ship queue can
      // apply them to every subsequent order without re-prompting.
      // Weight goes out per-unit (order_qty-normalized) so a qty=1
      // order and a qty=3 order share the same "2 lb per unit" seed.
      if (typeof onDimsCommitted === 'function') {
        try {
          onDimsCommitted({
            weight_oz_per_unit: Math.max(orderQty, 1) > 0
              ? weightOzTotal / Math.max(orderQty, 1)
              : weightOzTotal,
            length_in: parseFloat(length) || null,
            width_in:  parseFloat(width)  || null,
            height_in: parseFloat(height) || null,
          });
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      const body = e instanceof ApiError ? (e.body || {}) : {};
      // Backend signals "cookies expired, send the operator to settings"
      // via needs_session_sync — surface that as a dedicated UI state
      // instead of a generic error string.
      if (body.needs_session_sync) {
        setSessionExpired(true);
        setQuoteError(null);
      } else {
        const msg = body.error
          || (e instanceof ApiError ? e.message : String(e));
        setQuoteError(msg);
      }
    } finally {
      setQuoting(false);
    }
  };

  // Buy the chosen rate via BidRL backend. Backend POSTs to eBay's
  // Seller Hub label-service using stored cookies, gets back the
  // shipment id + tracking# + label URL, persists tracking on the
  // sale row, and returns the summary we render below. The operator's
  // chrome extension picks the new shipment up from /history within
  // ~1s and auto-prints the PDF — no eBay UI involvement at all.
  const buyLabel = async () => {
    if (!selectedRateId) return;
    const selectedRate = rates.find((r) => r.rate_id === selectedRateId);
    if (!selectedRate) {
      toastError("No rate selected", "Pick a rate before buying.");
      return;
    }
    // Build the purchase payload once — used by both the sync path (dialog
    // waits + shows step 3) and the async path (dialog hands off + closes).
    const payload = {
      // Echo the full rate so backend has carrier_code / service_code /
      // package_code without having to re-look-them-up.
      rate:              selectedRate,
      shipping_quote_id: quoteId,
      sale_id:           row.sale_id || null,
      lot_id:            row.lot_id || "",
      // External (OrderChecker) rows have no BidRL lot — they carry
      // the eBay listing id instead. Backend uses it to write the
      // dims into external_label_log so the next external sale of
      // the same listing pre-fills.
      legacy_item_id:    row.legacy_item_id || "",
      order_id:          row.order_id,
      line_item_id:      row.line_item_id,
      order_qty:         row.order_qty || 1,
      title:             row.cleaned_title || row.title || "",
      order_total:       row.sold_price ?? null,
      weight_oz:         weightOzTotal,
      length_in:         parseFloat(length) || null,
      width_in:          parseFloat(width)  || null,
      height_in:         parseFloat(height) || null,
      label_size:        "LS_4X6",
      signature_required: signatureRequired,
      insurance_required: insuranceRequired,
      // Mirror what we used at quote time so eBay's accepted cost
      // matches the operator's selection (otherwise the round-tripped
      // insurance entry's maxCompensationAmount disagrees with the
      // quote and the purchase 400s).
      insured_value:      insuranceRequired
        ? (Number(row.merchandise_value) > 0
            ? Number(row.merchandise_value)
            : (Number(row.total_paid) > 0
              ? Number(row.total_paid)
              : (row.sold_price ?? null)))
        : null,
    };

    // Async mode — hand the payload to the parent and close immediately.
    // The parent tracks in-flight purchases and restores the card on
    // failure (see UnfulfilledSales pendingPurchases). No spinner, no
    // step 3 — success/failure surfaces as a toast on the underlying
    // page while the operator moves on to the next order.
    if (typeof onSubmitAsync === 'function') {
      // Mirror dims into the listing cache immediately so the next
      // card the operator opens (either next in the bulk queue or a
      // sibling elsewhere on the page) pre-fills with these values —
      // we're not going to hear back from the backend for another 5-15s.
      saveListingDims(row, {
        weight_oz_per_unit: orderQty > 0 ? weightOzTotal / orderQty : weightOzTotal,
        length_in:    parseFloat(length) || null,
        width_in:     parseFloat(width)  || null,
        height_in:    parseFloat(height) || null,
        package_type: selectedRate.package_code || null,
      });
      try {
        onSubmitAsync(payload);
      } catch (e) {
        console.error("onSubmitAsync threw:", e);
      }
      onClose?.();
      return;
    }

    setPurchasing(true);
    setPurchaseError(null);
    try {
      const data = await apiPost('/api/shipping_purchase', payload);
      setPurchased(data);
      setStep(3);
      toastSuccess(
        "Label purchased",
        `${data.carrier_name || carrierLabel(data.carrier_code)} · ${data.tracking_number || ''}`
      );
      // Mirror what the backend wrote (per-unit weight, dims as-is) into
      // localStorage so any sibling unfulfilled card still on the page
      // — including ones the operator opens before the page refreshes —
      // pre-fills with these values.
      saveListingDims(row, {
        weight_oz_per_unit: orderQty > 0 ? weightOzTotal / orderQty : weightOzTotal,
        length_in:    parseFloat(length) || null,
        width_in:     parseFloat(width)  || null,
        height_in:    parseFloat(height) || null,
        package_type: selectedRate.package_code || null,
      });
      if (typeof onPurchased === 'function') onPurchased(data);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body || {}) : {};
      if (body.needs_session_sync) {
        setSessionExpired(true);
      } else {
        const msg = body.error
          || (e instanceof ApiError ? e.message : String(e));
        // Stash the raw eBay response so the dialog can render an
        // expandable "View eBay response" panel inline with the error
        // toast — operator can copy/paste it to me without screen-
        // scraping a Flask log on the server.
        setPurchaseError({ message: msg, ebay_raw: body.ebay_raw || null });
        toastError("Label purchase failed", msg);
      }
    } finally {
      setPurchasing(false);
    }
  };

  // Opens the purchased label PDF in a new tab. eBay's labelDownloadUrl
  // requires the same session cookies, so we DON'T just window.open()
  // — instead we link to it directly; the browser's cookie jar for
  // ebay.com is what it needs. The operator's extension already
  // auto-printed via SumatraPDF, this is just for manual re-print.
  const openLabelPdf = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"
      // Closing mid-purchase would leave us in a weird state — block it.
      disableEscapeKeyDown={purchasing}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShippingOutlinedIcon />
        <Box sx={{ flex: 1 }}>
          Buy eBay label
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Order {row?.order_id || ''}
            {row?.source === "external"
              ? ' · External (OrderChecker)'
              : ` · Lot ${row?.lot_id || ''}`}
          </Typography>
        </Box>
        {/* Escape hatch — when the BidRL flow fails or the operator
            needs to do something manual (refund, switch carrier eBay
            won't quote, add an insurance amount we don't support),
            jump straight to the Seller Hub page for this order. */}
        {row?.order_id && (
          <Button
            size="small"
            variant="outlined"
            component="a"
            href={`https://www.ebay.com/ship/single/${row.order_id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none' }}
          >
            Open in eBay
          </Button>
        )}
        <IconButton onClick={onClose} disabled={purchasing} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Destination address. Read-only here — pulled from eBay order. */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography sx={{ fontSize: 11, textTransform: 'uppercase',
            letterSpacing: 0.6, color: 'text.secondary', mb: 0.5 }}>
            Ship to
          </Typography>
          {shipToValid ? (
            <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line' }}>
              {shipTo.full_name}{"\n"}
              {shipTo.address1}{shipTo.address2 ? ` ${shipTo.address2}` : ""}{"\n"}
              {shipTo.city}, {shipTo.state} {shipTo.postal_code}
              {shipTo.country && shipTo.country !== 'US' ? `\n${shipTo.country}` : ""}
            </Typography>
          ) : (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              eBay didn't return a full shipping address for this order.
              Refresh the unfulfilled list or check eBay Seller Hub.
            </Alert>
          )}
        </Paper>

        {/* ─── STEP 1 — dimensions ─── */}
        {step === 1 && (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}
              flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.6, color: 'text.secondary' }}>
                Package
              </Typography>
              {/* Source badge — labels honestly reflect where the dims
                  currently shown came from. Old code lied about this
                  (called the cache "eBay listing"), which made it
                  impossible to tell why the dims didn't match what the
                  operator had typed on the listing-edit form. */}
              {prefillFromLocal ? (
                <Chip size="small" color="success" variant="outlined"
                  label={orderQty > 1
                    ? `Filled from last label (× ${orderQty})`
                    : 'Filled from last label'}
                  sx={{ height: 20, fontSize: 11 }} />
              ) : dimsSource === 'ebay_inventory' ? (
                <Chip size="small" color="success" variant="outlined"
                  label="Filled from eBay Inventory API"
                  sx={{ height: 20, fontSize: 11 }} />
              ) : dimsSource === 'items_table' ? (
                <Chip size="small" color="warning" variant="outlined"
                  label="Filled from local cache (may be stale)"
                  sx={{ height: 20, fontSize: 11 }} />
              ) : dimsSource === 'sibling_local' ? (
                <Chip size="small" color="warning" variant="outlined"
                  label="Filled from sibling-lot cache"
                  sx={{ height: 20, fontSize: 11 }} />
              ) : dimsSource === 'preset' ? (
                <Chip size="small" color="info" variant="outlined"
                  label="Filled from saved preset"
                  sx={{ height: 20, fontSize: 11 }} />
              ) : dimsSource === 'fallback_default' ? (
                <Chip size="small" color="warning" variant="outlined"
                  label="Filled from default (2 lb · 12×9×6) — verify"
                  sx={{ height: 20, fontSize: 11 }} />
              ) : null}
              {fetchingDims && (
                <Stack direction="row" alignItems="center" spacing={0.5}
                  sx={{ color: 'text.secondary' }}>
                  <CircularProgress size={11} />
                  <Typography sx={{ fontSize: 11 }}>
                    Checking eBay listing for dimensions…
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {PACKAGE_SIZES.map((p) => (
                <Chip key={p.label} label={p.label} size="small"
                  onClick={() => applyPreset(p)}
                  variant={
                    Number(length) === p.length &&
                    Number(width)  === p.width  &&
                    Number(height) === p.height ? 'filled' : 'outlined'
                  }
                  color={
                    Number(length) === p.length &&
                    Number(width)  === p.width  &&
                    Number(height) === p.height ? 'primary' : 'default'
                  } />
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 1.5 }}>
              <TextField label="Length" size="small" type="number" inputProps={{ step: 0.1, min: 0 }}
                value={length} onChange={(e) => setLength(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              <TextField label="Width" size="small" type="number" inputProps={{ step: 0.1, min: 0 }}
                value={width}  onChange={(e) => setWidth(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              <TextField label="Height" size="small" type="number" inputProps={{ step: 0.1, min: 0 }}
                value={height} onChange={(e) => setHeight(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <TextField label="Pounds" size="small" type="number" inputProps={{ step: 1, min: 0 }}
                value={weightLb} onChange={(e) => setWeightLb(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">lb</InputAdornment> }} />
              <TextField label="Ounces" size="small" type="number" inputProps={{ step: 0.1, min: 0, max: 15.99 }}
                value={weightOzExtra} onChange={(e) => setWeightOzExtra(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">oz</InputAdornment> }} />
              {weightValid && (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  = {weightOzTotal.toFixed(2)} oz total
                </Typography>
              )}
            </Stack>

            {sessionExpired && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <b>eBay session expired.</b> Open the eBay tab in your browser
                (so cookies refresh), then click the extension button to push
                cookies back to BidRL. Then hit "Get rates" again.
              </Alert>
            )}
            {quoteError && (
              <Alert severity="error" sx={{ mt: 1 }}>{quoteError}</Alert>
            )}
          </Box>
        )}

        {/* ─── STEP 2 — pick a rate ─── */}
        {step === 2 && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.6, color: 'text.secondary' }}>
                Select a rate
              </Typography>
              {/* Current dims summary + inline "edit" for the bulk-ship
                  flow. The operator committed dimensions once for the
                  queue; this row surfaces what those values are and
                  lets them override for THIS order (or update the
                  shared dims for the rest of the queue) without
                  hunting for a Back button. */}
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    `${(weightOzTotal / 16).toFixed(weightOzTotal % 16 === 0 ? 0 : 2)} lb · ` +
                    `${length}×${width}×${height}`
                  }
                  sx={{ height: 22, fontWeight: 500 }}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setStep(1)}
                  disabled={purchasing || quoting}
                  sx={{ minWidth: 0, textTransform: 'none' }}
                >
                  Edit dimensions
                </Button>
              </Stack>
            </Stack>
            {(() => {
              // Per-carrier counts power the filter chips' label
              // ("USPS · 4") so the operator knows at a glance which
              // carriers eBay returned anything for. Computed inline
              // since rates is short and this only fires per render.
              const buckets = { usps: 0, fedex: 0, ups: 0, other: 0 };
              for (const r of sortedRates) buckets[carrierBucket(r.carrier_code)] += 1;
              const visibleRates = carrierFilter
                ? sortedRates.filter((r) => carrierBucket(r.carrier_code) === carrierFilter)
                : sortedRates;
              return (
                <>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                    {visibleRates.length} of {rates.length} option{rates.length === 1 ? '' : 's'} shown.
                    Cheapest first.
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Chip
                      size="small"
                      label={`All · ${rates.length}`}
                      onClick={() => setCarrierFilter(null)}
                      color={carrierFilter == null ? 'primary' : 'default'}
                      variant={carrierFilter == null ? 'filled' : 'outlined'}
                    />
                    {['usps', 'fedex', 'ups', 'other'].map((b) => {
                      if (buckets[b] === 0) return null;
                      const cs = CARRIER_STYLE[b];
                      const label = b === 'usps' ? 'USPS'
                                  : b === 'fedex' ? 'FedEx'
                                  : b === 'ups' ? 'UPS' : 'Other';
                      return (
                        <Chip
                          key={b}
                          size="small"
                          label={`${label} · ${buckets[b]}`}
                          onClick={() => setCarrierFilter(carrierFilter === b ? null : b)}
                          color={cs.color}
                          variant={carrierFilter === b ? 'filled' : 'outlined'}
                        />
                      );
                    })}
                  </Stack>
                </>
              );
            })()}
            {/* Add-on opt-ins (signature + insurance). Both auto-
                enable on high-value orders so the operator doesn't
                have to remember to flip them. The price column on
                each rate row reflects the delta when either is on. */}
            {[
              {
                key: 'signature',
                on: signatureRequired,
                setOn: setSignatureRequired,
                label: 'Require signature on delivery',
                threshold: SIGNATURE_AUTO_THRESHOLD,
                costField: 'signature_cost',
              },
              {
                key: 'insurance',
                on: insuranceRequired,
                setOn: setInsuranceRequired,
                label: `Insure shipment${row?.sold_price ? ` (${money(row.sold_price)})` : ''}`,
                threshold: INSURANCE_AUTO_THRESHOLD,
                costField: 'insurance_cost',
              },
            ].map((opt) => {
              const costs = (rates || [])
                .map((r) => Number(r[opt.costField]))
                .filter((n) => Number.isFinite(n) && n > 0);
              const minCost = costs.length ? Math.min(...costs) : null;
              const autoOn = Number(row?.sold_price) > opt.threshold;
              return (
                <Paper key={opt.key} variant="outlined" sx={{ p: 1, mb: 1,
                  bgcolor: opt.on ? 'success.50' : 'transparent' }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={opt.on}
                        onChange={(e) => opt.setOn(e.target.checked)}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography sx={{ fontWeight: 600 }}>
                          {opt.label}
                        </Typography>
                        {minCost !== null && (
                          <Chip size="small" color="success" variant="outlined"
                            label={`+ ${money(minCost)} per rate`} sx={{ height: 20 }} />
                        )}
                        {autoOn && (
                          <Tooltip title={`Auto-enabled because the sale price is over $${opt.threshold}.`}>
                            <Chip size="small" color="info" variant="outlined"
                              label="auto" sx={{ height: 20 }} />
                          </Tooltip>
                        )}
                      </Stack>
                    }
                  />
                </Paper>
              );
            })}

            <RadioGroup value={selectedRateId || ''} onChange={(e) => setSelectedRateId(e.target.value)}>
              <Stack spacing={1}>
                {(carrierFilter
                  ? sortedRates.filter((r) => carrierBucket(r.carrier_code) === carrierFilter)
                  : sortedRates
                ).map((r) => {
                  // eBay returns ISO timestamps for the delivery window.
                  // Render them as a short calendar range — operators
                  // care about the date, not "X business days".
                  const fmt = (iso) => {
                    if (!iso) return null;
                    try {
                      return new Date(iso).toLocaleDateString('en-US',
                        { month: 'short', day: 'numeric' });
                    } catch { return null; }
                  };
                  const dMin = fmt(r.delivery_min);
                  const dMax = fmt(r.delivery_max);
                  const eta = dMin && dMax
                    ? (dMin === dMax ? `Delivers ${dMin}` : `Delivers ${dMin} – ${dMax}`)
                    : (dMax ? `Delivers by ${dMax}` : '');
                  const selected = selectedRateId === r.rate_id;
                  const showRetail = r.retail_cost > r.base_cost + 0.005;
                  const bucket = carrierBucket(r.carrier_code);
                  const accent = CARRIER_STYLE[bucket].hex;
                  return (
                    <Paper key={r.rate_id} variant="outlined"
                      onClick={() => setSelectedRateId(r.rate_id)}
                      sx={{
                        p: 1.25, cursor: 'pointer', position: 'relative',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderWidth: selected ? 2 : 1,
                        // 4px carrier-coloured stripe along the left edge —
                        // visible whether or not the row is selected so the
                        // operator can scan the list by carrier without
                        // expanding it.
                        borderLeft: `4px solid ${accent}`,
                        '&:hover': { borderColor: 'primary.light' },
                      }}>
                      <FormControlLabel
                        value={r.rate_id}
                        control={<Radio size="small" />}
                        sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                        label={
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, width: '100%' }}>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography sx={{ fontWeight: 600 }}>
                                  {r.carrier_name || carrierLabel(r.carrier_code)}
                                  {' · '}
                                  {r.service_name || serviceLabel(r.service_code)}
                                </Typography>
                                {r.buyer_selected && (
                                  <Chip size="small" color="info" label="Buyer's choice"
                                    sx={{ height: 18, fontSize: 10 }} />
                                )}
                              </Stack>
                              {eta && (
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                  {eta}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              {(() => {
                                const sigDelta = signatureRequired && Number(r.signature_cost) > 0 ? Number(r.signature_cost) : 0;
                                const insDelta = insuranceRequired && Number(r.insurance_cost) > 0 ? Number(r.insurance_cost) : 0;
                                const total = r.base_cost + sigDelta + insDelta;
                                const parts = [];
                                if (sigDelta) parts.push(`${money(sigDelta, r.signature_currency || r.cost_currency)} sig`);
                                if (insDelta) parts.push(`${money(insDelta, r.insurance_currency || r.cost_currency)} ins`);
                                return (
                                  <>
                                    <Typography sx={{
                                      fontWeight: 700, fontSize: 16,
                                      fontVariantNumeric: 'tabular-nums',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {money(total, r.cost_currency)}
                                    </Typography>
                                    {parts.length > 0 && (
                                      <Typography sx={{
                                        fontSize: 11, color: 'success.main',
                                        fontVariantNumeric: 'tabular-nums',
                                      }}>
                                        incl. {parts.join(' + ')}
                                      </Typography>
                                    )}
                                  </>
                                );
                              })()}
                              {showRetail && (
                                <Typography sx={{
                                  fontSize: 11, color: 'text.secondary',
                                  textDecoration: 'line-through',
                                  fontVariantNumeric: 'tabular-nums',
                                }}>
                                  {money(r.retail_cost, r.cost_currency)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </Paper>
                  );
                })}
              </Stack>
            </RadioGroup>
            {/* Purchase failure inline — shown next to the rate list so the
                operator can fix and retry without losing context. The
                <details> tag holds the raw eBay response for inspection
                when the human-readable message isn't enough. */}
            {purchaseError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  {purchaseError.message}
                </Typography>
                {row?.order_id && (
                  <Typography sx={{ fontSize: 13, mt: 0.5, mb: 0.5 }}>
                    Buy this label manually in Seller Hub:{' '}
                    <Box
                      component="a"
                      href={`https://www.ebay.com/ship/single/${row.order_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 600 }}
                    >
                      ebay.com/ship/single/{row.order_id}
                    </Box>
                  </Typography>
                )}
                {purchaseError.ebay_raw && (
                  <Box component="details" sx={{ mt: 0.5 }}>
                    <Box component="summary" sx={{ cursor: 'pointer',
                      fontSize: 12, color: 'text.secondary' }}>
                      View eBay response
                    </Box>
                    <Box component="pre" sx={{
                      mt: 0.5, p: 1, bgcolor: '#1e1e1e', color: '#dcdcdc',
                      fontSize: 11, borderRadius: 1, overflow: 'auto',
                      maxHeight: 300, maxWidth: '100%',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    }}>
                      {JSON.stringify(purchaseError.ebay_raw, null, 2)}
                    </Box>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        )}

        {/* ─── STEP 3 — purchase success ─── */}
        {step === 3 && purchased && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 2, textAlign: 'center' }}>
            <CheckCircleOutlineOutlinedIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h5">Label purchased</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {purchased.carrier_name || carrierLabel(purchased.carrier_code)}
              {purchased.service_name ? ` · ${purchased.service_name}` : ''}
              {purchased.label_cost != null && (
                <> · <b>{money(purchased.label_cost, purchased.label_currency)}</b></>
              )}
            </Typography>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Tracking number
              </Typography>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18 }}>
                {purchased.tracking_number || '—'}
              </Typography>
            </Box>
            {/* The chrome extension auto-prints the label as soon as eBay's
                /history endpoint reflects the new shipment (~1s). The PDF
                button here is a manual fallback / re-print option.
                ``printDetected`` flips true when the local label_server's
                last_saved_path changes after the purchase started — that's
                concrete proof the print pipeline ran end-to-end (eBay
                → extension → label_server → SumatraPDF). Without this
                the user just had to trust the "extension is running"
                green chip, which didn't actually confirm anything fired. */}
            {extHealth?.reachable && printDetected ? (
              <Alert severity="success" sx={{ mt: 0.5, maxWidth: 480 }}>
                Label received by local print server.
                {extHealth?.printer_name && extHealth.printer_name !== '(system default)' && (
                  <> Printer: <b>{extHealth.printer_name}</b>.</>
                )}
                {extHealth?.last_saved_path && (
                  <Box sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: 11,
                             color: 'text.secondary', wordBreak: 'break-all' }}>
                    Saved to: {extHealth.last_saved_path}
                  </Box>
                )}
              </Alert>
            ) : extHealth?.reachable ? (
              <Alert severity="info" sx={{ mt: 0.5, maxWidth: 480 }}>
                Print extension is running — waiting for the label to flow
                through eBay → extension → local print server…
                {extHealth?.last_saved_path && (
                  <Box sx={{ mt: 0.5, fontSize: 11, color: 'text.secondary' }}>
                    Last saved label on this machine:
                    <Box component="span" sx={{ fontFamily: 'monospace',
                                                ml: 0.5, wordBreak: 'break-all' }}>
                      {extHealth.last_saved_path}
                    </Box>
                  </Box>
                )}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 0.5, maxWidth: 480 }}>
                Print extension isn't running on this machine
                ({extHealth?.error || 'no response from 127.0.0.1:5175'}).
                Use the button below to open the label PDF and print
                manually.
              </Alert>
            )}
            {purchased.label_url && (
              <Button
                variant={extHealth?.reachable ? "outlined" : "contained"}
                size="large"
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={() => openLabelPdf(purchased.label_url)}>
                Open label PDF
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {step === 1 && (
          <>
            <Button onClick={onClose} disabled={quoting}>Cancel</Button>
            <Button variant="contained" onClick={fetchQuote}
              disabled={quoting || !weightValid || !dimsValid || !shipToValid}
              startIcon={quoting ? <CircularProgress size={14} color="inherit" /> : null}>
              {quoting ? 'Fetching rates…' : 'Get rates'}
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)} disabled={purchasing}>Back</Button>
            <Button variant="contained" onClick={buyLabel}
              disabled={purchasing || !selectedRateId}
              startIcon={purchasing ? <CircularProgress size={14} color="inherit" /> : null}>
              {purchasing
                ? 'Buying label…'
                : (typeof onSubmitAsync === 'function'
                    ? (queueRemaining > 0
                        ? `Buy & continue (${queueRemaining} more)`
                        : 'Buy label & continue')
                    : 'Buy label')}
            </Button>
          </>
        )}
        {step === 3 && (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
